import type { PassportAttributes } from "./types.js";

/**
 * battery-v1 attrs → the EU DPP Registry's compact JSON, PUBLIC view, pinned to
 * the Commission's semantic artefacts core 1.0.0 + batteries 1.0.0 (+ evb/lmt/ind
 * sub-group 1.0.0), published 30 Oct 2026. The registry fetches this document
 * from the UPI and validates it with the JSON Schemas vendored in ./eu/1.0.0
 * (EN 18216: JSON is the one mandatory format). A new artefact release means a
 * new mapper version, never an in-place edit — registered passports cite 1.0.0.
 *
 * Restricted views (res1–res3) are out of scope: the registry validates the
 * public document it can fetch anonymously.
 */

const REPO = "https://repository.product-passport.ec.europa.eu";
const UNIT = `${REPO}/core/vocabs/measurement-unit#`;
const TEMPERATURE = `${REPO}/core/vocabs/observed-characteristic#temperature`;

export type EuBatterySubgroup = "EV" | "LMT" | "IND";

const SUBGROUP: Record<string, { code: EuBatterySubgroup; path: string; category: string }> = {
  ev: { code: "EV", path: "evb", category: "electricVehicleBattery" },
  lmt: { code: "LMT", path: "lmt", category: "lightMeansOfTransportBattery" },
  industrial: { code: "IND", path: "ind", category: "industrialBattery" },
};

// li-other / solid-state / other have no concept in battery-chemistry 1.0.0 —
// such passports cannot be registered until the vocabulary grows.
const CHEMISTRY: Record<string, string> = {
  "li-nmc": "lithiumIonNMC",
  "li-nca": "lithiumIonNCA",
  "li-lfp": "lithiumIonLFP",
  "li-lmo": "lithiumIonLMO",
  "sodium-ion": "sodiumIon",
  nimh: "nickelMetalHydride",
  "lead-acid": "leadAcid",
};

/** Free-text CRM names (EN + TR) → critical-raw-material 1.0.0 concepts. */
const CRM: [RegExp, string][] = [
  [/lith|lityum/, "lithium"],
  [/cobalt|kobalt/, "cobalt"],
  [/nickel|nikel/, "nickelBatteryGrade"],
  [/graphite|grafit/, "graphite"],
  [/mangan/, "manganese"],
  [/copper|bak[ıi]r/, "copper"],
  [/alumin|alümin/, "bauxiteAluminaAluminium"],
  [/phosphate|fosfat/, "phosphateRock"],
  [/phosph|fosfor/, "phosphorus"],
  [/silic|silis/, "siliconMetal"],
  [/magnes|magnez/, "magnesium"],
  [/vanad/, "vanadium"],
  [/titan/, "titaniumMetal"],
  [/antimon/, "antimony"],
  [/boron|bor\b/, "boron"],
  [/niob/, "niobium"],
  [/tantal/, "tantalum"],
  [/tungsten|volfram/, "tungsten"],
];

// ISO 3166-1 alpha-2 → alpha-3: the registry wants alpha-3, the platform stores alpha-2.
const ALPHA3 = Object.fromEntries(
  (
    "AD:AND AE:ARE AF:AFG AG:ATG AI:AIA AL:ALB AM:ARM AO:AGO AQ:ATA AR:ARG AS:ASM AT:AUT AU:AUS AW:ABW AX:ALA " +
    "AZ:AZE BA:BIH BB:BRB BD:BGD BE:BEL BF:BFA BG:BGR BH:BHR BI:BDI BJ:BEN BL:BLM BM:BMU BN:BRN BO:BOL BQ:BES " +
    "BR:BRA BS:BHS BT:BTN BV:BVT BW:BWA BY:BLR BZ:BLZ CA:CAN CC:CCK CD:COD CF:CAF CG:COG CH:CHE CI:CIV CK:COK " +
    "CL:CHL CM:CMR CN:CHN CO:COL CR:CRI CU:CUB CV:CPV CW:CUW CX:CXR CY:CYP CZ:CZE DE:DEU DJ:DJI DK:DNK DM:DMA " +
    "DO:DOM DZ:DZA EC:ECU EE:EST EG:EGY EH:ESH ER:ERI ES:ESP ET:ETH FI:FIN FJ:FJI FK:FLK FM:FSM FO:FRO FR:FRA " +
    "GA:GAB GB:GBR GD:GRD GE:GEO GF:GUF GG:GGY GH:GHA GI:GIB GL:GRL GM:GMB GN:GIN GP:GLP GQ:GNQ GR:GRC GS:SGS " +
    "GT:GTM GU:GUM GW:GNB GY:GUY HK:HKG HM:HMD HN:HND HR:HRV HT:HTI HU:HUN ID:IDN IE:IRL IL:ISR IM:IMN IN:IND " +
    "IO:IOT IQ:IRQ IR:IRN IS:ISL IT:ITA JE:JEY JM:JAM JO:JOR JP:JPN KE:KEN KG:KGZ KH:KHM KI:KIR KM:COM KN:KNA " +
    "KP:PRK KR:KOR KW:KWT KY:CYM KZ:KAZ LA:LAO LB:LBN LC:LCA LI:LIE LK:LKA LR:LBR LS:LSO LT:LTU LU:LUX LV:LVA " +
    "LY:LBY MA:MAR MC:MCO MD:MDA ME:MNE MF:MAF MG:MDG MH:MHL MK:MKD ML:MLI MM:MMR MN:MNG MO:MAC MP:MNP MQ:MTQ " +
    "MR:MRT MS:MSR MT:MLT MU:MUS MV:MDV MW:MWI MX:MEX MY:MYS MZ:MOZ NA:NAM NC:NCL NE:NER NF:NFK NG:NGA NI:NIC " +
    "NL:NLD NO:NOR NP:NPL NR:NRU NU:NIU NZ:NZL OM:OMN PA:PAN PE:PER PF:PYF PG:PNG PH:PHL PK:PAK PL:POL PM:SPM " +
    "PN:PCN PR:PRI PS:PSE PT:PRT PW:PLW PY:PRY QA:QAT RE:REU RO:ROU RS:SRB RU:RUS RW:RWA SA:SAU SB:SLB SC:SYC " +
    "SD:SDN SE:SWE SG:SGP SH:SHN SI:SVN SJ:SJM SK:SVK SL:SLE SM:SMR SN:SEN SO:SOM SR:SUR SS:SSD ST:STP SV:SLV " +
    "SX:SXM SY:SYR SZ:SWZ TC:TCA TD:TCD TF:ATF TG:TGO TH:THA TJ:TJK TK:TKL TL:TLS TM:TKM TN:TUN TO:TON TR:TUR " +
    "TT:TTO TV:TUV TW:TWN TZ:TZA UA:UKR UG:UGA UM:UMI US:USA UY:URY UZ:UZB VA:VAT VC:VCT VE:VEN VG:VGB VI:VIR " +
    "VN:VNM VU:VUT WF:WLF WS:WSM YE:YEM YT:MYT ZA:ZAF ZM:ZMB ZW:ZWE"
  )
    .split(" ")
    .map((pair) => pair.split(":") as [string, string]),
);

export function toAlpha3(country: string): string | undefined {
  const c = country.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(c) ? c : ALPHA3[c];
}

/** Top-level keys pub-batteries 1.0.0 requires (core keys are always emitted). */
const REQUIRED = [
  "registrantIdentity",
  "manufacturerIdentification",
  "manufacturerPostalAddress",
  "batteryCategory",
  "batteryIdentification",
  "manufacturingPlace",
  "manufacturingDate",
  "batteryWeight",
  "batteryCapacity",
  "batteryChemistry",
  "hazardousSubstances",
  "extinguishingAgent",
  "criticalRawMaterials",
  "renewableContentShare",
  "minimalVoltage",
  "nominalVoltage",
  "maximumVoltage",
  "originalPowerCapability",
  "powerLimit",
  "notInUseTemperatureRange",
  "internalCellResistance",
  "internalPackResistance",
  "separateCollectionSymbol",
  "euDeclarationOfConformity",
  "wasteBatteryPreventionManagementInfo",
];
const SUBGROUP_REQUIRED: Record<EuBatterySubgroup, string[]> = {
  EV: [
    "expectedLifetimeCycles",
    "cycleLifeReferenceTest",
    "capacityExhaustionThreshold",
    "initialRoundTripEnergyEfficiency",
    "roundTripEfficiencyAtHalfCycleLife",
    "cycleLifeChargeCRate",
  ],
  LMT: [
    "expectedLifetimeCycles",
    "cycleLifeReferenceTest",
    "initialRoundTripEnergyEfficiency",
    "roundTripEfficiencyAtHalfCycleLife",
    "cycleLifeChargeCRate",
  ],
  IND: [],
};
/** EU waste-info sub-key → battery-v1 field (Art. 74(1)(a)–(f)); all ten are required. */
const WASTE_INFO: [string, string][] = [
  ["endUserRoleInWastePrevention", "circularity.wastePreventionInfo"],
  ["endUserRoleInSeparateCollection", "circularity.separateCollectionInfo"],
  ["separateCollectionPoints", "circularity.separateCollectionPoints"],
  ["takeBackCollectionPoints", "circularity.takeBackProgramUrl"],
  ["preparationForReuseFacilities", "circularity.reuseFacilities"],
  ["preparationForRepurposingFacilities", "circularity.repurposingFacilities"],
  ["wasteBatteryTreatmentFacilities", "circularity.treatmentFacilities"],
  ["wasteBatterySafetyInstructions", "circularity.wasteSafetyInstructions"],
  ["batteryLabelSymbolMeaning", "labels.labelsExplanationUrl"],
  ["batterySubstancesImpact", "circularity.substancesImpact"],
];

export interface EuBatteryMeta {
  /** DPP instance id — stable per passport (the published passport URL). */
  dppId: string;
  /** The registered UPI, exactly as submitted. */
  upi: string;
  granularity: "item" | "batch" | "model";
  /** ISO 8601 date-time of the served version. */
  lastUpdate: string;
  /** false once the passport is withdrawn/archived → instance-status#invalidStatus. */
  valid: boolean;
  /** Art. 77(4) identity of who registers / is responsible for the passport. */
  registrantIdentity: string;
  /** "xx-XX" tag for free text that is not recognisably Turkish (multiLanguage key). */
  textLanguage: string;
  /** Public manufacturer operator block (Annex VI Part A 1). */
  manufacturer?:
    | {
        tradeName?: string | undefined;
        postalAddress?: string | undefined;
        website?: string | undefined;
        email?: string | undefined;
      }
    | undefined;
}

export interface EuBatteryDpp {
  doc: Record<string, unknown>;
  /** Registry productSubGroup, null when the battery category is unset/unknown. */
  subgroup: EuBatterySubgroup | null;
  /** EU keys that could not be filled — non-empty means the registry WILL reject it. */
  missing: string[];
}

export function toEuBatteryDpp(attrs: PassportAttributes, meta: EuBatteryMeta): EuBatteryDpp {
  const str = (id: string): string | undefined => {
    const v = attrs[id];
    return typeof v === "string" && v.trim() !== "" ? v.trim() : undefined;
  };
  const num = (id: string): number | undefined => {
    const v = attrs[id];
    return typeof v === "number" && Number.isFinite(v) ? v : undefined;
  };
  const unitValue = (value: number | undefined, unit: string) =>
    value === undefined ? undefined : { unit: UNIT + unit, value };
  // Attrs carry no language tag; Turkish-specific letters are a reliable tell for
  // the exporters this platform serves, everything else gets the default tag.
  const ml = (text: string | undefined) =>
    text === undefined ? undefined : { [/[çğışöüÇĞİŞÖÜ]/.test(text) ? "tr-TR" : meta.textLanguage]: text };

  const tMin = num("performance.operatingTempMinC");
  const tMax = num("performance.operatingTempMaxC");
  // The voltages and power limit are declared over the operating range; EU
  // requires that range on each value (Annex XIII 1(h)/(i) "temperature ranges").
  const range = (lo: number | undefined, hi: number | undefined) =>
    lo === undefined || hi === undefined
      ? undefined
      : { observedCharacteristic: TEMPERATURE, lowerBound: lo, upperBound: hi, unit: `${UNIT}degreeCelsius` };
  const opRange = range(tMin, tMax);
  const ranged = (value: number | undefined, unit: string) =>
    value === undefined || opRange === undefined
      ? undefined
      : { unit: UNIT + unit, value, valueRange: opRange };

  const sub = SUBGROUP[str("general.batteryCategory") ?? ""];
  const chemistry = CHEMISTRY[str("general.batteryChemistry") ?? ""];
  const country = str("general.manufacturingCountry");
  const country3 = country ? toAlpha3(country) : undefined;
  const crmText = (str("materialComposition.criticalRawMaterials") ?? "").toLowerCase();
  const crms = [...new Set(CRM.filter(([re]) => re.test(crmText)).map(([, c]) => c))];
  const hazardous = str("materialComposition.hazardousSubstances");
  const docUrl = str("labels.declarationOfConformityUrl");
  const modelId = str("general.modelIdentifier") ?? str("general.batteryModel");
  const gtin = str("general.gtin");
  const mfgDate = str("general.manufacturingDate");
  const cellMohm = num("performance.internalResistanceCellMohm");
  const packMohm = num("performance.internalResistancePackMohm");
  const warranty = num("general.warrantyPeriodMonths");
  const storage = range(num("performance.storageTempMinC"), num("performance.storageTempMaxC"));
  const storageTest = str("performance.storageTempTestReference");
  const powerLimit = ranged(num("performance.powerLimitW"), "watt");
  const symbols = [
    ...(attrs["materialComposition.cadmiumAboveThreshold"] === true ? ["cadmiumSymbol"] : []),
    ...(attrs["materialComposition.leadAboveThreshold"] === true ? ["leadSymbol"] : []),
  ];
  const m = meta.manufacturer;
  const mfgName = str("general.manufacturerName");

  const waste: Record<string, unknown> = {};
  for (const [key, field] of WASTE_INFO) {
    const text = str(field);
    if (text !== undefined) waste[key] = ml(text);
  }

  const fields: Record<string, unknown> = {
    registrantIdentity: meta.registrantIdentity || undefined,
    manufacturerIdentification:
      mfgName || m?.tradeName
        ? {
            ...(mfgName ? { manufacturerName: mfgName } : {}),
            ...(m?.tradeName ? { manufacturerTradeName: m.tradeName } : {}),
          }
        : undefined,
    manufacturerPostalAddress: m?.postalAddress,
    manufacturerContactDetails:
      m?.website || m?.email
        ? {
            ...(m.website ? { manufacturerWebsite: m.website } : {}),
            ...(m.email ? { manufacturerEmail: `mailto:${m.email}` } : {}),
          }
        : undefined,
    batteryCategory: sub ? `${REPO}/product/batteries/vocabs/battery-category#${sub.category}` : undefined,
    // modelIdentifier is only valid paired with batchNumber (dependentRequired);
    // a model/GTIN-level passport identifies itself through the other two keys.
    batteryIdentification:
      modelId || gtin
        ? {
            ...(gtin ? { productNumber: gtin } : {}),
            ...(modelId ? { otherIdentificationElement: modelId } : {}),
          }
        : undefined,
    manufacturingPlace: country3
      ? { manufacturingCity: str("general.manufacturingPlace") ?? country3, manufacturingCountry: country3 }
      : undefined,
    manufacturingDate: mfgDate && /^\d{4}-\d{2}/.test(mfgDate) ? mfgDate.slice(0, 7) : undefined,
    batteryWeight: unitValue(num("general.batteryWeightKg"), "kilogram"),
    batteryCapacity: unitValue(num("performance.ratedCapacityAh"), "ampereHour"),
    batteryChemistry: chemistry
      ? [`${REPO}/product/batteries/vocabs/battery-chemistry#${chemistry}`]
      : undefined,
    hazardousSubstances: hazardous
      ? [
          ...new Set(
            hazardous
              .split(/[,;\n]/)
              .map((s) => s.trim())
              .filter(Boolean),
          ),
        ]
      : undefined,
    extinguishingAgent: ml(str("circularity.extinguishingAgent")),
    criticalRawMaterials:
      crms.length > 0 ? crms.map((c) => `${REPO}/core/vocabs/critical-raw-material#${c}`) : undefined,
    renewableContentShare: unitValue(num("circularity.renewableContentSharePct"), "percent"),
    minimalVoltage: ranged(num("general.minVoltageV"), "volt"),
    nominalVoltage: ranged(num("general.nominalVoltageV"), "volt"),
    maximumVoltage: ranged(num("general.maxVoltageV"), "volt"),
    originalPowerCapability: unitValue(num("performance.nominalPowerW"), "watt"),
    powerLimit: powerLimit ? [powerLimit] : undefined,
    notInUseTemperatureRange:
      storage && storageTest
        ? { notInUseTemperatureRangeTest: ml(storageTest), valueRange: storage }
        : undefined,
    commercialWarrantyPeriod: warranty === undefined ? undefined : Math.round(warranty),
    internalCellResistance: unitValue(cellMohm === undefined ? undefined : cellMohm / 1000, "ohm"),
    internalPackResistance: unitValue(packMohm === undefined ? undefined : packMohm / 1000, "ohm"),
    cycleLifeDischargeCRate: unitValue(num("performance.cycleLifeDischargeCRate"), "reciprocalHour"),
    // const true in the schema: a passport declaring "no symbol" is not registrable.
    separateCollectionSymbol: attrs["labels.separateCollectionSymbol"] === true ? true : undefined,
    chemicalSymbol:
      symbols.length > 0
        ? symbols.map((s) => `${REPO}/product/batteries/vocabs/battery-chemical-symbol#${s}`)
        : undefined,
    euDeclarationOfConformity: docUrl
      ? {
          resourceTitle: "EU declaration of conformity",
          contentType: /\.pdf($|[?#])/i.test(docUrl) ? "application/pdf" : "text/html",
          url: docUrl,
        }
      : undefined,
    wasteBatteryPreventionManagementInfo: Object.keys(waste).length > 0 ? waste : undefined,
  };

  if (sub && sub.code !== "IND") {
    const cycles = num("performance.cycleLife");
    Object.assign(fields, {
      expectedLifetimeCycles: cycles === undefined || cycles < 1 ? undefined : Math.round(cycles),
      cycleLifeReferenceTest: ml(str("performance.cycleLifeTestReference")),
      initialRoundTripEnergyEfficiency: unitValue(num("performance.roundTripEfficiencyPct"), "percent"),
      roundTripEfficiencyAtHalfCycleLife: unitValue(
        num("performance.roundTripEfficiencyAt50PctCycleLifePct"),
        "percent",
      ),
      cycleLifeChargeCRate: unitValue(num("performance.cycleLifeChargeCRate"), "reciprocalHour"),
      ...(sub.code === "EV"
        ? {
            capacityExhaustionThreshold: unitValue(
              num("performance.capacityThresholdForExhaustionPct"),
              "percent",
            ),
          }
        : {}),
    });
  }

  const doc: Record<string, unknown> = {
    digitalProductPassportId: meta.dppId,
    uniqueProductIdentifier: meta.upi,
    granularity: `${REPO}/core/vocabs/granularity-level#${meta.granularity}`,
    dppStatus: `${REPO}/core/vocabs/instance-status#${meta.valid ? "validStatus" : "invalidStatus"}`,
    lastUpdate: meta.lastUpdate,
    contentSpecificationIds: [
      `${REPO}/core/1.0.0`,
      `${REPO}/product/batteries/1.0.0`,
      ...(sub ? [`${REPO}/product/batteries/subgroup/${sub.path}/1.0.0`] : []),
    ],
  };
  for (const [key, value] of Object.entries(fields)) if (value !== undefined) doc[key] = value;

  const required = [...REQUIRED, ...(sub ? SUBGROUP_REQUIRED[sub.code] : [])];
  const missing = required.filter((k) => doc[k] === undefined);
  if (doc.wasteBatteryPreventionManagementInfo !== undefined) {
    for (const [key] of WASTE_INFO)
      if (!(key in waste)) missing.push(`wasteBatteryPreventionManagementInfo.${key}`);
  }
  return { doc, subgroup: sub?.code ?? null, missing };
}

/**
 * EU key (as reported in `missing`) → the battery-v1 fields that feed it, so the
 * panel can name what to fill. Keys absent here come from outside the attrs:
 * registrantIdentity (registry enrolment), manufacturerPostalAddress (the
 * passport's public manufacturer operator).
 */
export const EU_BATTERY_SOURCES: Record<string, string[]> = {
  manufacturerIdentification: ["general.manufacturerName"],
  batteryCategory: ["general.batteryCategory"],
  batteryIdentification: ["general.modelIdentifier", "general.gtin"],
  manufacturingPlace: ["general.manufacturingCountry"],
  manufacturingDate: ["general.manufacturingDate"],
  batteryWeight: ["general.batteryWeightKg"],
  batteryCapacity: ["performance.ratedCapacityAh"],
  batteryChemistry: ["general.batteryChemistry"],
  hazardousSubstances: ["materialComposition.hazardousSubstances"],
  extinguishingAgent: ["circularity.extinguishingAgent"],
  criticalRawMaterials: ["materialComposition.criticalRawMaterials"],
  renewableContentShare: ["circularity.renewableContentSharePct"],
  minimalVoltage: ["general.minVoltageV", "performance.operatingTempMinC", "performance.operatingTempMaxC"],
  nominalVoltage: [
    "general.nominalVoltageV",
    "performance.operatingTempMinC",
    "performance.operatingTempMaxC",
  ],
  maximumVoltage: ["general.maxVoltageV", "performance.operatingTempMinC", "performance.operatingTempMaxC"],
  originalPowerCapability: ["performance.nominalPowerW"],
  powerLimit: ["performance.powerLimitW", "performance.operatingTempMinC", "performance.operatingTempMaxC"],
  notInUseTemperatureRange: [
    "performance.storageTempMinC",
    "performance.storageTempMaxC",
    "performance.storageTempTestReference",
  ],
  internalCellResistance: ["performance.internalResistanceCellMohm"],
  internalPackResistance: ["performance.internalResistancePackMohm"],
  separateCollectionSymbol: ["labels.separateCollectionSymbol"],
  euDeclarationOfConformity: ["labels.declarationOfConformityUrl"],
  wasteBatteryPreventionManagementInfo: WASTE_INFO.map(([, field]) => field),
  ...Object.fromEntries(
    WASTE_INFO.map(([key, field]) => [`wasteBatteryPreventionManagementInfo.${key}`, [field]]),
  ),
  expectedLifetimeCycles: ["performance.cycleLife"],
  cycleLifeReferenceTest: ["performance.cycleLifeTestReference"],
  capacityExhaustionThreshold: ["performance.capacityThresholdForExhaustionPct"],
  initialRoundTripEnergyEfficiency: ["performance.roundTripEfficiencyPct"],
  roundTripEfficiencyAtHalfCycleLife: ["performance.roundTripEfficiencyAt50PctCycleLifePct"],
  cycleLifeChargeCRate: ["performance.cycleLifeChargeCRate"],
};
